import { CartUpdateEvent } from "@theme/events";

class TeeinblueCartBridge {
  constructor() {
    this.lastCartCount = this.getCurrentCartCount();
    this.init();
  }

  init() {
    document.addEventListener("click", event => {
      if (event.target.closest("button, [role='button'], a")) this.syncPendingNwfProperties();
    }, true);

    document.addEventListener("submit", event => {
      const form = event.target;
      if (
        form.classList.contains("tib-product-form") ||
        form.querySelector('[name="properties[_tib_id]"]') ||
        form.action?.includes("/cart/add")
      ) {
        this.syncPendingNwfProperties();
        setTimeout(() => this.checkCartUpdate(), 500);
        setTimeout(() => this.checkCartUpdate(), 1000);
        setTimeout(() => this.checkCartUpdate(), 1500);
      }
    }, true);

    this.interceptFetch();
  }

  getCurrentCartCount() {
    const cartBubble = document.querySelector('[data-testid="cart-drawer-trigger"] .cart-bubble__count');
    return cartBubble ? parseInt(cartBubble.textContent || "0", 10) : 0;
  }

  storageGet(key) {
    try {
      return localStorage.getItem(key) || "";
    } catch (err) {
      return "";
    }
  }

  getNwfData() {
    const clean = value => String(value || "").replace(/\s+/g, " ").trim();
    const formatDefinition = value => {
      const text = clean(value)
        .replace(/^\*?adjective\*?\.\s*/i, "")
        .replace(/^adj\.\s*/i, "")
        .replace(/^\*?verb\*?\.\s*/i, "")
        .replace(/^v\.\s*/i, "");
      return text ? `Adjective. ${text}` : "";
    };

    return {
      word: clean(this.storageGet("nwf_word")),
      pronunciation: clean(this.storageGet("nwf_pronunciation")),
      definition: formatDefinition(this.storageGet("nwf_definition") || this.storageGet("nwf_description")),
      example: clean(this.storageGet("nwf_sample_sentence") || this.storageGet("nwf_example"))
    };
  }

  shouldGoToCheckout() {
    try {
      return localStorage.getItem("nwf_checkout_after_add") === "1";
    } catch (err) {
      return false;
    }
  }

  clearCheckoutFlag() {
    try {
      localStorage.removeItem("nwf_checkout_after_add");
    } catch (err) {
      console.warn("Unable to clear Nerd Word checkout intent.", err);
    }
  }

  isLegacyVisibleProperty(name, value = "") {
    const key = String(name || "").trim().toLowerCase();
    const text = String(value || "");

    if (!key) return false;
    if (key.startsWith("_")) return false;
    if (/origin/.test(key)) return true;
    if (/forged\s+from/i.test(text)) return true;

    return false;
  }

  sanitizeProperties(properties = {}) {
    const data = this.getNwfData();
    const sanitized = {};

    if (data.word) sanitized["New Word"] = data.word;
    if (data.pronunciation) sanitized.Pronunciation = data.pronunciation;
    if (data.definition) sanitized.Definition = data.definition;
    if (data.example) sanitized.Example = data.example;

    Object.entries(properties || {}).forEach(([name, value]) => {
      if (this.isLegacyVisibleProperty(name, value)) return;
      if (["New Word", "Pronunciation", "Definition", "Example"].includes(name)) return;
      sanitized[name] = value;
    });

    return sanitized;
  }

  syncPendingNwfProperties() {
    try {
      localStorage.removeItem("nwf_origin");
      sessionStorage.removeItem("nwf_origin");
    } catch (err) {
      console.warn("Unable to clear retired Nerd Word properties.", err);
    }
  }

  sanitizeJsonBody(body) {
    try {
      const payload = JSON.parse(body);
      payload.properties = this.sanitizeProperties(payload.properties || {});
      return JSON.stringify(payload);
    } catch (err) {
      return body;
    }
  }

  sanitizeFormData(body) {
    const data = this.getNwfData();
    const next = new FormData();

    if (data.word) next.append("properties[New Word]", data.word);
    if (data.pronunciation) next.append("properties[Pronunciation]", data.pronunciation);
    if (data.definition) next.append("properties[Definition]", data.definition);
    if (data.example) next.append("properties[Example]", data.example);

    body.forEach((value, name) => {
      const propertyMatch = String(name).match(/^properties\[(.*)\]$/);
      const propertyName = propertyMatch?.[1] || "";
      if (propertyMatch && this.isLegacyVisibleProperty(propertyName, value)) return;
      if (["New Word", "Pronunciation", "Definition", "Example"].includes(propertyName)) return;
      next.append(name, value);
    });

    return next;
  }

  sanitizeUrlSearchParams(body) {
    const data = this.getNwfData();
    const next = new URLSearchParams();

    if (data.word) next.append("properties[New Word]", data.word);
    if (data.pronunciation) next.append("properties[Pronunciation]", data.pronunciation);
    if (data.definition) next.append("properties[Definition]", data.definition);
    if (data.example) next.append("properties[Example]", data.example);

    body.forEach((value, name) => {
      const propertyMatch = String(name).match(/^properties\[(.*)\]$/);
      const propertyName = propertyMatch?.[1] || "";
      if (propertyMatch && this.isLegacyVisibleProperty(propertyName, value)) return;
      if (["New Word", "Pronunciation", "Definition", "Example"].includes(propertyName)) return;
      next.append(name, value);
    });

    return next;
  }

  sanitizeCartAddArgs(args) {
    const [input, init = {}] = args;
    const nextInit = { ...init };
    const body = nextInit.body;

    if (typeof body === "string") {
      const trimmed = body.trim();
      nextInit.body = trimmed.startsWith("{")
        ? this.sanitizeJsonBody(body)
        : this.sanitizeUrlSearchParams(new URLSearchParams(body)).toString();
      return [input, nextInit];
    }

    if (body instanceof FormData) {
      nextInit.body = this.sanitizeFormData(body);
      return [input, nextInit];
    }

    if (body instanceof URLSearchParams) {
      nextInit.body = this.sanitizeUrlSearchParams(body);
      return [input, nextInit];
    }

    return args;
  }

  async checkCartUpdate() {
    try {
      const cart = await (await fetch("/cart.js")).json();
      const newCount = cart.item_count;

      if (newCount !== this.lastCartCount) {
        const itemsAdded = newCount - this.lastCartCount;
        this.lastCartCount = newCount;

        document.dispatchEvent(new CartUpdateEvent(cart, "teeinblue-bridge", {
          source: "teeinblue",
          itemCount: itemsAdded
        }));

        if (itemsAdded > 0 && this.shouldGoToCheckout()) {
          this.clearCheckoutFlag();
          window.location.assign("/checkout");
        }
      }
    } catch (error) {
      console.error("Error checking cart:", error);
    }
  }

  interceptFetch() {
    const originalFetch = window.fetch;

    window.fetch = (...args) => {
      const [input] = args;
      const requestUrl = typeof input === "string" ? input : input?.url;

      if (String(requestUrl || "").includes("/cart/add")) {
        this.syncPendingNwfProperties();
        const nextArgs = this.sanitizeCartAddArgs(args);
        const addItem = () => originalFetch(...nextArgs).then(response => {
          if (response.ok) setTimeout(() => this.checkCartUpdate(), 300);
          return response;
        });

        if (this.shouldGoToCheckout()) {
          return originalFetch("/cart/clear.js", { method: "POST" })
            .catch(error => {
              console.warn("Unable to clear stale Nerd Word cart before adding the corrected mug.", error);
            })
            .then(() => {
              this.lastCartCount = 0;
              return addItem();
            });
        }

        return addItem();
      }

      return originalFetch(...args);
    };
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new TeeinblueCartBridge();
  });
} else {
  new TeeinblueCartBridge();
}
